// Reusable USP section component

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/utilities/cn';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  PLAYER_COLOR_FAMILIES,
  getColorStop,
  type PlayerColorFamily,
} from '@/styles/playerColors';

interface UspItem {
  icon?: LucideIcon;
  bigText: string;
  smallText: string;
  iconColor?: string;
  accentFamily?: PlayerColorFamily;
}

interface UspSectionProps {
  usps: UspItem[];
  columns?: number;
  mobileColumns?: number;
  fullWidth?: boolean;
}

const DEFAULT_USP_ACCENT_FAMILY: PlayerColorFamily = 'lime';
const DEFAULT_USP_ACCENT_SEQUENCE: PlayerColorFamily[] = [
  'orange',
  'lime',
  'cyan',
  'indigo',
  'fuchsia',
];

const getMobileGridClass = (count: number) => {
  switch (count) {
    case 2:
      return 'grid-cols-2';
    case 3:
      return 'grid-cols-3';
    case 1:
    default:
      return 'grid-cols-1';
  }
};

const getDesktopGridClass = (count: number) => {
  switch (count) {
    case 3:
      return 'md:grid-cols-2 lg:grid-cols-3';
    case 2:
      return 'md:grid-cols-2';
    case 1:
    default:
      return 'md:grid-cols-1';
  }
};

const getDefaultAccentFamily = (
  index: number,
  total: number
): PlayerColorFamily => {
  if (total <= DEFAULT_USP_ACCENT_SEQUENCE.length) {
    return DEFAULT_USP_ACCENT_SEQUENCE[index] ?? DEFAULT_USP_ACCENT_FAMILY;
  }

  const maxIndex = PLAYER_COLOR_FAMILIES.length - 1;
  const position =
    total === 1 ? 0 : Math.round((index / (total - 1)) * maxIndex);

  return PLAYER_COLOR_FAMILIES[position] ?? DEFAULT_USP_ACCENT_FAMILY;
};

const UspSection: React.FC<UspSectionProps> = ({
  usps,
  columns = 1,
  mobileColumns = 1,
  fullWidth = false,
}) => {
  return (
    <div
      className={cn(
        'grid w-full gap-4',
        getMobileGridClass(mobileColumns),
        getDesktopGridClass(columns),
        fullWidth
          ? 'max-w-none order-1'
          : 'order-2 w-[88vw] max-w-none flex-1 md:order-1 md:w-full md:max-w-[50%]'
      )}
    >
      {usps.map((usp, index) => {
        const IconComponent = usp.icon;
        const accentFamily =
          usp.accentFamily ?? getDefaultAccentFamily(index, usps.length);
        const accentInk = usp.iconColor ?? getColorStop(accentFamily, '400');
        return (
          <Card
            key={index}
            className="h-full gap-0 border-border/80 py-0 shadow-sm"
          >
            <CardHeader className="gap-0 px-6 pt-6 pb-0">
              <div className="grid grid-cols-[auto_1fr] items-center gap-4">
                {IconComponent && (
                  <div
                    className="flex shrink-0 items-center justify-start"
                    style={{ color: accentInk }}
                  >
                    <IconComponent size={32} />
                  </div>
                )}
                <CardTitle className="text-lg leading-[1.3] md:text-2xl">
                  {usp.bigText}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent
              className={cn(
                'px-6 pb-6 pt-2',
                IconComponent ? 'pl-[4.5rem]' : 'pl-6'
              )}
            >
              <CardDescription className="text-base leading-[1.6]">
                {usp.smallText}
              </CardDescription>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default UspSection;
